import React from 'react';
import { LayoutDashboard, Users, GitBranch, Sparkles, Settings, ChevronLeft, ChevronDown, ChevronsDownUp, X, ShieldCheck, ScrollText, Palmtree, GraduationCap, MessageSquareHeart, Briefcase, ListChecks, Target, TrendingUp, Coins, BarChart3, Award, Cake, FolderArchive, Grid3x3, LogOut, Gavel } from 'lucide-react';

export type AtlasView = 'dashboard' | 'colaboradores' | 'organograma' | 'disc' | 'ferias' | 'treinamentos' | 'onboarding' | 'feedback' | 'desempenho' | 'pdi' | 'disciplinar' | 'vagas' | 'cargos' | 'pesquisas' | 'kudos' | 'lembretes' | 'documentos' | 'sucessao' | 'offboarding' | 'configuracoes' | 'permissoes' | 'auditoria';

interface ItemDef {
  id: AtlasView;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  badge?: string;
  adminOnly?: boolean;
}

interface GroupDef {
  id: string;
  label: string | null; // null = item solto, sem cabeçalho de grupo
  adminOnly?: boolean;
  itens: ItemDef[];
}

const GRUPOS: GroupDef[] = [
  {
    id: 'inicio',
    label: null,
    itens: [
      { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
    ],
  },
  {
    id: 'pessoas',
    label: 'Pessoas',
    itens: [
      { id: 'colaboradores', label: 'Colaboradores',     icon: <Users className="w-4 h-4" /> },
      { id: 'organograma',   label: 'Organograma',       icon: <GitBranch className="w-4 h-4" /> },
      { id: 'cargos',        label: 'Cargos & Salários',  icon: <Coins className="w-4 h-4" /> },
      { id: 'documentos',    label: 'Documentos',         icon: <FolderArchive className="w-4 h-4" /> },
    ],
  },
  {
    id: 'jornada',
    label: 'Recrutamento & Jornada',
    itens: [
      { id: 'vagas',       label: 'Vagas',       icon: <Briefcase className="w-4 h-4" /> },
      { id: 'onboarding',  label: 'Onboarding',  icon: <ListChecks className="w-4 h-4" /> },
      { id: 'ferias',      label: 'Férias',      icon: <Palmtree className="w-4 h-4" /> },
      { id: 'offboarding', label: 'Offboarding', icon: <LogOut className="w-4 h-4" /> },
    ],
  },
  {
    id: 'desenvolvimento',
    label: 'Desenvolvimento',
    itens: [
      { id: 'disc',         label: 'Perfil DISC',      icon: <Sparkles className="w-4 h-4" /> },
      { id: 'treinamentos', label: 'Treinamentos',     icon: <GraduationCap className="w-4 h-4" /> },
      { id: 'desempenho',   label: 'Desempenho',       icon: <Target className="w-4 h-4" /> },
      { id: 'pdi',          label: 'PDI',              icon: <TrendingUp className="w-4 h-4" /> },
      { id: 'feedback',     label: 'Feedback 360',     icon: <MessageSquareHeart className="w-4 h-4" /> },
      { id: 'disciplinar',  label: 'Ocorrências',      icon: <Gavel className="w-4 h-4" /> },
      { id: 'sucessao',     label: 'Sucessão & 9-box', icon: <Grid3x3 className="w-4 h-4" /> },
    ],
  },
  {
    id: 'engajamento',
    label: 'Engajamento',
    itens: [
      { id: 'pesquisas', label: 'Clima & eNPS',    icon: <BarChart3 className="w-4 h-4" /> },
      { id: 'kudos',     label: 'Reconhecimento',  icon: <Award className="w-4 h-4" /> },
      { id: 'lembretes', label: 'Aniversariantes', icon: <Cake className="w-4 h-4" /> },
    ],
  },
  {
    id: 'administracao',
    label: 'Administração',
    adminOnly: true,
    itens: [
      { id: 'permissoes',    label: 'Permissões',    icon: <ShieldCheck className="w-4 h-4" /> },
      { id: 'auditoria',     label: 'Auditoria',     icon: <ScrollText className="w-4 h-4" /> },
      { id: 'configuracoes', label: 'Configurações', icon: <Settings className="w-4 h-4" /> },
    ],
  },
];

interface SidebarProps {
  current: AtlasView;
  onChange: (v: AtlasView) => void;
  collapsed: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

// Detecta breakpoint desktop (>= lg). No mobile a sidebar é sempre expandida (drawer).
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

const AtlasSidebar: React.FC<SidebarProps> = ({ current, onChange, collapsed, onToggle, isAdmin, mobileOpen = false, onMobileClose }) => {
  const isDesktop = useIsDesktop();
  // No mobile o drawer sempre aparece expandido; collapsed só vale no desktop
  const collapsedEff = isDesktop && collapsed;
  const grupos = GRUPOS
    .filter(g => !g.adminOnly || isAdmin)
    .map(g => ({ ...g, itens: g.itens.filter(i => !i.adminOnly || isAdmin) }))
    .filter(g => g.itens.length > 0);

  // Grupo que contém o item ativo (para abrir automaticamente)
  const grupoAtivo = grupos.find(g => g.itens.some(i => i.id === current))?.id ?? null;

  const [abertos, setAbertos] = React.useState<Record<string, boolean>>(() =>
    Object.fromEntries(grupos.map(g => [g.id, g.id === grupoAtivo]))
  );

  // Garante que o grupo do item ativo esteja sempre aberto
  React.useEffect(() => {
    if (grupoAtivo) {
      setAbertos(prev => (prev[grupoAtivo] ? prev : { ...prev, [grupoAtivo]: true }));
    }
  }, [grupoAtivo]);

  const toggleGrupo = (id: string) =>
    setAbertos(prev => ({ ...prev, [id]: !prev[id] }));

  // Grupos que possuem cabeçalho (passíveis de recolher)
  const gruposComLabel = grupos.filter(g => g.label);
  const todosAbertos = gruposComLabel.length > 0 && gruposComLabel.every(g => abertos[g.id]);
  const toggleTodos = () =>
    setAbertos(Object.fromEntries(gruposComLabel.map(g => [g.id, !todosAbertos])));

  const renderItem = (item: ItemDef) => {
    const ativo = current === item.id;
    return (
      <button
        key={item.id}
        onClick={() => {
          if (item.disabled) return;
          onChange(item.id);
          if (!isDesktop) onMobileClose?.();
        }}
        disabled={item.disabled}
        title={collapsedEff ? item.label : undefined}
        className={`w-full flex items-center ${collapsedEff ? 'justify-center px-0' : 'gap-3 px-3'} py-2 rounded-lg text-sm font-medium transition ${
          ativo
            ? 'bg-purple-50 text-purple-700'
            : item.disabled
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
      >
        <span className={ativo ? 'text-purple-600' : ''}>{item.icon}</span>
        {!collapsedEff && (
          <>
            <span className="flex-1 text-left truncate">{item.label}</span>
            {item.badge && (
              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700">
                {item.badge}
              </span>
            )}
          </>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Backdrop (mobile) */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`bg-white border-r border-gray-200 flex flex-col transition-transform duration-200 ${
          collapsedEff ? 'w-16' : 'w-60'
        } fixed inset-y-0 left-0 z-40 lg:static lg:z-auto lg:translate-x-0 lg:transition-all ${
          mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className={`h-16 flex items-center gap-3 border-b border-gray-100 ${collapsedEff ? 'justify-center px-0' : 'px-3'}`}>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center shadow-md flex-shrink-0">
            <span className="text-white font-black text-xl">A</span>
          </div>
          {!collapsedEff && (
            <div className="min-w-0 flex-1">
              <h1 className="font-bold text-gray-900 text-base leading-tight">Atlas</h1>
              <p className="text-[10px] text-gray-400 leading-tight">HCM · Gestão de Pessoas</p>
            </div>
          )}
          {/* Fechar (somente mobile) */}
          <button
            onClick={onMobileClose}
            className="lg:hidden p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition flex-shrink-0"
            title="Fechar menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
          {!collapsedEff && gruposComLabel.length > 0 && (
            <button
              onClick={toggleTodos}
              className="w-full flex items-center justify-end gap-1 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-300 hover:text-gray-500 transition"
              title={todosAbertos ? 'Recolher tudo' : 'Expandir tudo'}
            >
              {todosAbertos ? 'Recolher tudo' : 'Expandir tudo'}
              <ChevronsDownUp className={`w-3 h-3 ${todosAbertos ? '' : 'rotate-180'}`} />
            </button>
          )}
          {collapsedEff
            ? // Recolhido: só ícones, sem cabeçalhos de grupo
              grupos.flatMap(g => g.itens).map(renderItem)
            : grupos.map(grupo => {
                // Item solto (sem rótulo de grupo)
                if (!grupo.label) {
                  return <div key={grupo.id}>{grupo.itens.map(renderItem)}</div>;
                }
                const aberto = abertos[grupo.id] ?? true;
                return (
                  <div key={grupo.id} className="pt-1">
                    <button
                      onClick={() => toggleGrupo(grupo.id)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition"
                    >
                      <span className="flex-1 text-left">{grupo.label}</span>
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform ${aberto ? '' : '-rotate-90'}`}
                      />
                    </button>
                    {aberto && <div className="space-y-1 mt-0.5">{grupo.itens.map(renderItem)}</div>}
                  </div>
                );
              })}
        </nav>

        {/* Toggle (recolher barra) — só no desktop */}
        <button
          onClick={onToggle}
          className="hidden lg:flex h-10 border-t border-gray-100 text-gray-400 hover:text-gray-700 hover:bg-gray-50 items-center justify-center transition"
          title={collapsed ? 'Expandir' : 'Recolher'}
        >
          <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </aside>
    </>
  );
};

export default AtlasSidebar;
